import React from "react";
import { X, MessageCircle, Phone, CheckCircle2 } from "lucide-react";
import * as Dialog from "@radix-ui/react-dialog";
import { COMPANY } from "../data/constants";
import { buildWhatsAppUrl } from "../lib/whatsapp";

export default function ServiceDetailDialog({ service, open, onOpenChange }) {
  if (!service) return null;
  const whatsappMsg = `Hi Shoolin Digital, I'd like to know more about your ${service.name} offering.`;
  const whatsappUrl = buildWhatsAppUrl(COMPANY.whatsapp, whatsappMsg);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          data-testid="service-dialog-overlay"
          className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0"
        />
        <Dialog.Content
          data-testid={`service-dialog-${service.id}`}
          className="fixed left-1/2 top-1/2 z-[80] w-[95vw] max-w-4xl -translate-x-1/2 -translate-y-1/2 max-h-[92vh] overflow-y-auto bg-[#0B0F19] border border-[#F47B20]/40 shadow-2xl shadow-black/80 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          {/* Image strip */}
          <div className="relative h-56 md:h-72 overflow-hidden border-b border-white/10">
            <img
              src={service.image}
              alt={service.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/40 to-transparent" />
            <Dialog.Close
              data-testid="service-dialog-close"
              className="absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 bg-black/70 border border-white/20 text-white hover:bg-[#F47B20] hover:text-black hover:border-[#F47B20] transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </Dialog.Close>
            <div className="absolute bottom-5 left-5 right-5">
              <span className="inline-block px-3 py-1 border border-[#F47B20]/50 text-[#F47B20] text-[10px] font-bold uppercase tracking-[0.3em] bg-[#0B0F19]/70 backdrop-blur-md">
                {service.category}
              </span>
              <Dialog.Title asChild>
                <h2 className="mt-3 font-display text-3xl md:text-4xl font-black text-white uppercase tracking-tighter leading-[0.95]">
                  {service.name}
                </h2>
              </Dialog.Title>
              <Dialog.Description className="mt-2 text-white/70 text-sm md:text-base">
                {service.tagline}
              </Dialog.Description>
            </div>
          </div>

          {/* Body */}
          <div className="p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {/* Features */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F47B20] mb-4">
                What's Included
              </p>
              <ul className="space-y-3">
                {service.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/85 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#F47B20] mt-1 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>

            {/* Applications */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F47B20] mb-4">
                Where It Fits
              </p>
              <ul className="space-y-3">
                {service.applications.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/85 text-sm">
                    <span className="mt-1 inline-block w-2 h-2 bg-[#F47B20] flex-shrink-0" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>

            {/* Spec table */}
            <div className="md:col-span-2">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F47B20] mb-4">
                Engagement Details
              </p>
              <table className="w-full border-t border-l border-white/10">
                <tbody>
                  {service.specs.map((s, i) => (
                    <tr key={i} className="border-b border-white/10">
                      <td className="border-r border-white/10 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-white/55 w-1/3 align-top">
                        {s.k}
                      </td>
                      <td className="border-r border-white/10 px-4 py-3 text-sm text-white/85">
                        {s.v}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* CTA bar */}
          <div className="border-t border-white/10 p-5 md:p-6 flex flex-col sm:flex-row gap-3 bg-[#0B0F19]/80">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`service-dialog-whatsapp-${service.id}`}
              className="flex-1 inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#25D366] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#1FBA59] transition-colors"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp Enquiry
            </a>
            <a
              href={`tel:${COMPANY.phone}`}
              data-testid={`service-dialog-call-${service.id}`}
              className="flex-1 inline-flex items-center justify-center gap-3 px-6 py-4 bg-[#F47B20] text-black font-bold uppercase tracking-[0.2em] text-xs hover:bg-[#FF8A33] transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call {COMPANY.phoneDisplay}
            </a>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
